import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, TextField, Button, MenuItem, Alert,
  CircularProgress, FormControl, InputLabel, Select,
  Paper, Avatar, Skeleton, Divider, Chip, Tooltip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import {
  DirectionsBus as BusIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  EventSeat as SeatIcon,
  Badge as BadgeIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchBusById,
  updateBus,
  clearUpdateError,
  selectUpdateBusError,
  selectBusFieldErrors,
  selectIsUpdatingBus
} from '../slices/busesSlice';
import { fetchStaffMinimal } from '../slices/staffSlice';
import { motion } from 'framer-motion';

// ── helpers ──────────────────────────────────────────────────────────────────

const MotionPaper = motion(Paper);
const FONT = 'Cairo, Tajawal, sans-serif';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, ease: 'easeOut', delay },
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    fontFamily: FONT,
    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
  },
  '& .MuiInputLabel-root': { fontWeight: 500, fontFamily: FONT, '&.Mui-focused': { color: '#4338CA' } },
  '& .MuiFormHelperText-root': { fontFamily: FONT },
};

const SectionLabel = ({ children }) => (
  <Typography fontWeight={700} sx={{ color: '#1E1B4B', mb: 1.5, display: 'block', fontSize: '0.95rem', fontFamily: FONT }}>
    {children}
  </Typography>
);

// ── component ─────────────────────────────────────────────────────────────────

const busTypes = ['داخلي', 'خارجي'];

const EditBusPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const dispatch = useDispatch();

  const { selectedBus: busData, selectedBusStatus } = useSelector((state) => state.buses);
  const { minimal: staffMinimal, minimalStatus } = useSelector((state) => state.staff);
  const updateError = useSelector(selectUpdateBusError);
  const fieldErrors = useSelector(selectBusFieldErrors);
  const isUpdating = useSelector(selectIsUpdatingBus);

  const [bus, setBus] = useState({
    name: '', plateNumber: '', capacity: '', type: '',
    driver: '', managerName: '', phone: '',
  });
  const [initError, setInitError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    dispatch(fetchStaffMinimal({ is_archived: false }));
    dispatch(clearUpdateError());
    dispatch(fetchBusById(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (busData && staffMinimal.length > 0) {
      let driverId = '';
      if (busData.bus_type === 'داخلي' && busData.driver_name) {
        const matched = staffMinimal.find(s =>
          !s.is_archived && s.is_driver && s.full_name.trim() === busData.driver_name.trim()
        );
        if (matched) driverId = matched.id;
      }
      setBus({
        name: busData.name || '',
        plateNumber: busData.bus_number || '',
        capacity: busData.capacity || '',
        type: busData.bus_type || '',
        driver: driverId,
        managerName: busData.manager_name || '',
        phone: busData.phone_number || '',
      });
      setDataLoaded(true);
    }
  }, [busData, staffMinimal]);

  useEffect(() => {
    if (busData && minimalStatus !== 'loading' && !dataLoaded) {
      setBus({
        name: busData.name || '',
        plateNumber: busData.bus_number || '',
        capacity: busData.capacity || '',
        type: busData.bus_type || '',
        driver: '',
        managerName: busData.manager_name || '',
        phone: busData.phone_number || '',
      });
      setDataLoaded(true);
    }
  }, [busData, minimalStatus, dataLoaded]);

  const getFieldError = (fieldName) => {
    const map = {
      name: 'name', plateNumber: 'bus_number', driver: 'driver',
      type: 'bus_type', capacity: 'capacity', managerName: 'manager_name', phone: 'phone_number'
    };
    return fieldErrors?.[map[fieldName]];
  };

  const hasFieldErrors = () => fieldErrors && Object.keys(fieldErrors).length > 0;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'capacity' && (parseInt(value) < 1 || isNaN(value))) return;
    setBus(prev => ({ ...prev, [name]: value }));
    if (getFieldError(name)) dispatch(clearUpdateError());
    if (initError) setInitError(null);
  };

  const handleTypeChange = (_, newType) => {
    if (!newType) return;
    setBus(prev => ({ ...prev, type: newType, driver: '', managerName: '', phone: '' }));
    if (initError) setInitError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!bus.name.trim()) return setInitError('اسم الحافلة مطلوب');
    if (!bus.plateNumber.trim()) return setInitError('رقم اللوحة مطلوب');
    if (!bus.type) return setInitError('نوع الحافلة مطلوب');
    if (!bus.capacity || parseInt(bus.capacity) < 1) return setInitError('السعة مطلوبة ويجب أن تكون أكبر من صفر');
    if (bus.type === 'داخلي' && !bus.driver) return setInitError('السائق مطلوب للحافلات الداخلية');
    if (bus.type === 'خارجي') {
      if (!bus.managerName.trim()) return setInitError('اسم المسؤول مطلوب للحافلات الخارجية');
      if (!bus.phone.trim()) return setInitError('رقم الهاتف مطلوب للحافلات الخارجية');
    }

    setInitError(null);
    dispatch(clearUpdateError());

    const payload = {
      name: bus.name.trim(),
      bus_number: bus.plateNumber.trim(),
      bus_type: bus.type,
      capacity: parseInt(bus.capacity),
    };
    if (bus.type === 'داخلي') {
      payload.driver = bus.driver || null;
      payload.manager_name = null;
      payload.phone_number = null;
    } else {
      payload.manager_name = bus.managerName.trim();
      payload.phone_number = bus.phone.trim();
      payload.driver = null;
    }

    try {
      await dispatch(updateBus({ id, data: payload })).unwrap();
      showToast('تم تحديث بيانات الحافلة بنجاح!', 'success');
      navigate('/buses');
    } catch {}
  };

  const availableDrivers = staffMinimal.filter(e => !e.is_archived && e.is_driver);
  const selectedDriver = availableDrivers.find(d => d.id === bus.driver);

  // ── states ────────────────────────────────────────────────────────────────

  if (!dataLoaded || selectedBusStatus === 'loading') {
    return (
      <Box dir="rtl" display="flex" flexDirection="column" justifyContent="center" alignItems="center"
        minHeight="100vh" gap={2} sx={{ bgcolor: '#F8F9FF', fontFamily: FONT }}>
        <CircularProgress size={48} thickness={3} sx={{ color: '#4338CA' }} />
        <Typography sx={{ color: '#6B7280', fontFamily: FONT, fontWeight: 600 }}>جاري تحميل بيانات الحافلة…</Typography>
      </Box>
    );
  }

  if (selectedBusStatus === 'failed' || !busData) {
    return (
      <Box dir="rtl" sx={{ p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: '#F8F9FF', fontFamily: FONT }}>
        <Box sx={{ maxWidth: 480, mx: 'auto', textAlign: 'center', mt: 8 }}>
          <Alert severity="error" sx={{ borderRadius: 3, mb: 2, fontFamily: FONT }}>الحافلة المطلوبة غير موجودة</Alert>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/buses')}
            sx={{
              borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT,
              borderColor: '#4338CA', color: '#4338CA',
              '&:hover': { borderColor: '#3730A3', bgcolor: '#EEF2FF' },
            }}>
            العودة إلى قائمة الحافلات
          </Button>
        </Box>
      </Box>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    // 🎨 Design System Applied
    <Box dir="rtl" sx={{
      direction: 'rtl', fontFamily: FONT,
      p: { xs: 2, md: 3 }, minHeight: '100vh', bgcolor: '#F8F9FF',
    }}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>

      {/* ── Hero Header ──────────────────────────────────────────────────── */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          p: { xs: 2.5, sm: 3.5 }, mb: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          color: '#fff', position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{
            position: 'absolute', right: -60, top: -60,
            width: 230, height: 230, borderRadius: '50%',
            background: 'rgba(255,255,255,0.07)', pointerEvents: 'none',
          }} />
          <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between" spacing={2}>
            <Stack useFlexGap direction="row" alignItems="center" spacing={2}>
              <Box sx={{
                width: 60, height: 60, borderRadius: 2.5,
                bgcolor: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <EditIcon sx={{ fontSize: 30, color: '#fff' }} />
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={800} color="#fff" lineHeight={1.2} sx={{ fontFamily: FONT }}>
                  تعديل بيانات الحافلة
                </Typography>
                <Stack direction="row" spacing={1} mt={0.75} flexWrap="wrap" useFlexGap>
                  <Chip label={busData.name || busData.bus_number} size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.22)', color: '#fff', fontWeight: 700, fontSize: '0.72rem', fontFamily: FONT }} />
                  <Chip label={busData.bus_type === 'داخلي' ? 'داخلي' : 'خارجي'} size="small"
                    sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '0.72rem', fontFamily: FONT }} />
                </Stack>
              </Box>
            </Stack>
            <Tooltip title="العودة">
              <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/buses')}
                sx={{
                  color: '#fff', borderColor: 'rgba(255,255,255,0.6)', borderRadius: 2.5,
                  fontWeight: 700, textTransform: 'none', fontFamily: FONT, px: 2.5, py: 1,
                  boxShadow: 'none',
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
                }}>
                العودة
              </Button>
            </Tooltip>
          </Stack>
      </MotionPaper>

      {/* ── Form Card ────────────────────────────────────────────────────── */}
      <motion.div {...fadeUp(0.1)}>
        <Paper elevation={0} sx={{
          borderRadius: 3, border: '1px solid #E5E7EB',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          bgcolor: '#fff', overflow: 'hidden', mb: 3,
        }}>
          <form onSubmit={handleSubmit}>

            {/* ── Error banners ────────────────────────────────────────── */}
            {(initError || (updateError?.message && !hasFieldErrors())) && (
              <Alert severity="error" icon={<WarningIcon />}
                sx={{
                  borderRadius: 0, borderBottom: '1px solid #FEE2E2', fontFamily: FONT,
                  bgcolor: '#FEE2E2', color: '#DC2626',
                  '& .MuiAlert-icon': { color: '#DC2626' },
                }}>
                {initError || updateError?.message}
              </Alert>
            )}

            <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
              <Stack spacing={3.5}>

                {/* ── Basic info ────────────────────────────────────── */}
                <Box>
                  <SectionLabel>المعلومات الأساسية</SectionLabel>
                  <Stack spacing={2}>
                    <TextField
                      label="اسم الحافلة"
                      name="name"
                      value={bus.name}
                      onChange={handleChange}
                      fullWidth required
                      error={!!getFieldError('name')}
                      helperText={getFieldError('name')}
                      disabled={isUpdating}
                      InputProps={{ startAdornment: <BusIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} /> }}
                      sx={fieldSx}
                    />
                    <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField
                        label="رقم اللوحة"
                        name="plateNumber"
                        value={bus.plateNumber}
                        onChange={handleChange}
                        fullWidth required
                        error={!!getFieldError('plateNumber')}
                        helperText={getFieldError('plateNumber')}
                        disabled={isUpdating}
                        InputProps={{ startAdornment: <BadgeIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} /> }}
                        sx={fieldSx}
                      />
                      <TextField
                        label="السعة (طالب)"
                        name="capacity"
                        type="number"
                        value={bus.capacity}
                        onChange={handleChange}
                        fullWidth required
                        error={!!getFieldError('capacity')}
                        helperText={getFieldError('capacity')}
                        disabled={isUpdating}
                        inputProps={{ min: 1 }}
                        InputProps={{ startAdornment: <SeatIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} /> }}
                        sx={fieldSx}
                      />
                    </Stack>
                  </Stack>
                </Box>

                <Divider sx={{ borderColor: '#E5E7EB' }} />

                {/* ── Bus type ─────────────────────────────────────── */}
                <Box>
                  <SectionLabel>نوع الحافلة</SectionLabel>
                  <ToggleButtonGroup
                    value={bus.type}
                    exclusive
                    onChange={handleTypeChange}
                    disabled={isUpdating}
                    sx={{ gap: 1 }}
                  >
                    {busTypes.map(t => (
                      <ToggleButton key={t} value={t} sx={{
                        borderRadius: '12px !important',
                        px: 4, fontWeight: 700, fontSize: '0.95rem', fontFamily: FONT,
                        color: '#6B7280',
                        border: '1.5px solid #E5E7EB !important',
                        textTransform: 'none',
                        '&.Mui-selected': {
                          background: 'linear-gradient(135deg,#4338CA,#5B21F4)',
                          color: '#fff',
                          borderColor: '#4338CA !important',
                          '&:hover': { background: 'linear-gradient(135deg,#3730A3,#4c1dd1)' },
                        },
                      }}>
                        {t === 'داخلي' ? '🏫 داخلي' : '🚌 خارجي'}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {getFieldError('type') && (
                    <Typography variant="caption" display="block" mt={0.5} sx={{ color: '#DC2626', fontFamily: FONT }}>
                      {getFieldError('type')}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: '#E5E7EB' }} />

                {/* ── Driver (internal) ────────────────────────────── */}
                {bus.type === 'داخلي' && (
                  <Box>
                    <SectionLabel>السائق</SectionLabel>
                    <FormControl fullWidth required error={!!getFieldError('driver')} disabled={isUpdating} sx={fieldSx}>
                      <InputLabel>اختر السائق</InputLabel>
                      <Select label="اختر السائق" name="driver" value={bus.driver} onChange={handleChange}
                        sx={{ borderRadius: 2.5 }}>
                        {minimalStatus === 'loading' ? (
                          <MenuItem disabled>
                            <Stack useFlexGap direction="row" spacing={2} alignItems="center" width="100%">
                              <Skeleton variant="circular" width={32} height={32} />
                              <Skeleton variant="text" width="60%" />
                            </Stack>
                          </MenuItem>
                        ) : availableDrivers.map(driver => (
                          <MenuItem key={driver.id} value={driver.id} sx={{ fontFamily: FONT }}>
                            <Stack useFlexGap direction="row" spacing={1.5} alignItems="center">
                              <Avatar sx={{ width: 30, height: 30, bgcolor: '#4338CA', fontSize: 12 }}>
                                {driver.full_name?.[0] || <AccountCircle />}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: FONT, color: '#1E1B4B' }}>{driver.full_name}</Typography>
                                {driver.employee_id && (
                                  <Typography variant="caption" color="text.secondary">
                                    #{driver.employee_id}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                      {getFieldError('driver') && (
                        <Typography variant="caption" sx={{ mt: 0.5, mr: 2, color: '#DC2626', fontFamily: FONT }}>
                          {getFieldError('driver')}
                        </Typography>
                      )}
                      {!bus.driver && busData?.driver_name && (
                        <Typography variant="caption" sx={{ mt: 0.5, mr: 2, color: '#0284C7', fontFamily: FONT }}>
                          السائق الحالي: {busData.driver_name}
                        </Typography>
                      )}
                      {availableDrivers.length === 0 && minimalStatus !== 'loading' && (
                        <Typography variant="caption" sx={{ mt: 0.5, mr: 2, color: '#D97706', fontFamily: FONT }}>
                          لا يوجد سائقون متاحون حالياً
                        </Typography>
                      )}
                    </FormControl>

                    {/* Driver preview card */}
                    {selectedDriver && (
                      <Paper elevation={0} sx={{
                        mt: 2, p: 2, borderRadius: 2.5,
                        bgcolor: '#EEF2FF',
                        border: '1px solid #E5E7EB',
                        display: 'flex', alignItems: 'center', gap: 2,
                      }}>
                        <Avatar sx={{ width: 44, height: 44, bgcolor: '#4338CA', fontWeight: 700 }}>
                          {selectedDriver.full_name?.[0]}
                        </Avatar>
                        <Box flex={1}>
                          <Typography fontWeight={700} sx={{ color: '#4338CA', fontFamily: FONT }}>{selectedDriver.full_name}</Typography>
                          {selectedDriver.employee_id && (
                            <Typography variant="caption" sx={{ color: '#6B7280', fontFamily: FONT }}>
                              رقم الموظف: {selectedDriver.employee_id}
                            </Typography>
                          )}
                        </Box>
                        <CheckIcon sx={{ color: '#16A34A', opacity: 0.9 }} />
                      </Paper>
                    )}
                  </Box>
                )}

                {/* ── Manager & phone (external) ───────────────────── */}
                {bus.type === 'خارجي' && (
                  <Box>
                    <SectionLabel>بيانات المسؤول الخارجي</SectionLabel>
                    <Stack spacing={2}>
                      <TextField
                        label="اسم المسؤول"
                        name="managerName"
                        value={bus.managerName}
                        onChange={handleChange}
                        fullWidth required
                        error={!!getFieldError('managerName')}
                        helperText={getFieldError('managerName')}
                        disabled={isUpdating}
                        InputProps={{ startAdornment: <PersonIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} /> }}
                        sx={fieldSx}
                      />
                      <TextField
                        label="رقم الهاتف"
                        name="phone"
                        value={bus.phone}
                        onChange={handleChange}
                        fullWidth required
                        error={!!getFieldError('phone')}
                        helperText={getFieldError('phone')}
                        disabled={isUpdating}
                        InputProps={{ startAdornment: <PhoneIcon sx={{ color: 'text.disabled', mr: 1, fontSize: 20 }} /> }}
                        sx={fieldSx}
                      />
                    </Stack>
                  </Box>
                )}

              </Stack>
            </Box>

            {/* ── Footer action bar ─────────────────────────────────────── */}
            <Box sx={{
              px: { xs: 2.5, sm: 3.5 }, py: 2.5,
              bgcolor: '#F8F9FF',
              borderTop: '1px solid #E5E7EB',
              display: 'flex', justifyContent: 'flex-end', gap: 1.5,
            }}>
              <Button
                variant="outlined"
                startIcon={<CloseIcon />}
                onClick={() => navigate('/buses')}
                disabled={isUpdating}
                sx={{
                  borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT,
                  borderColor: '#E5E7EB', color: '#6B7280',
                  '&:hover': { borderColor: '#6B7280', bgcolor: '#EEF2FF' },
                  minWidth: 110,
                }}
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={isUpdating ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                disabled={
                  isUpdating ||
                  (bus.type === 'داخلي' && availableDrivers.length === 0) ||
                  minimalStatus === 'loading'
                }
                sx={{
                  borderRadius: 2.5, textTransform: 'none', fontWeight: 700, fontFamily: FONT,
                  bgcolor: '#4338CA', color: '#fff',
                  boxShadow: 'none',
                  minWidth: 140,
                  '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: '#C7D2FE', color: '#fff' },
                }}
              >
                {isUpdating ? 'جاري الحفظ…' : 'حفظ التعديلات'}
              </Button>
            </Box>

          </form>
        </Paper>
      </motion.div>
      </Box>
    </Box>
  );
};

export default EditBusPage;
